import { api as apiClient } from './api';
import type {
  TrafficIndex,
  CreateTrafficIndexDto,
  UpdateTrafficIndexDto,
  TrafficIndexPermission,
  AddPermissionDto,
  UpdatePermissionDto,
} from '@/types/trafficIndex.types';

/**
 * Creates a new traffic index
 * @param data - The traffic index creation data
 * @returns Promise resolving to the created traffic index
 */
export const createTrafficIndex = async (
  data: CreateTrafficIndexDto
): Promise<TrafficIndex> => {
  const response = await apiClient.post<{ success: boolean; data: TrafficIndex }>(
    '/traffic-indexes',
    data
  );
  return response.data.data;
};

/**
 * Retrieves a specific traffic index by ID
 * @param id - The traffic index ID
 * @returns Promise resolving to the traffic index
 */
export const getTrafficIndex = async (id: string): Promise<TrafficIndex> => {
  const response = await apiClient.get<{ success: boolean; data: TrafficIndex }>(
    `/traffic-indexes/${id}`
  );
  return response.data.data;
};

/**
 * Retrieves all traffic indexes for the authenticated user
 * @returns Promise resolving to an array of traffic indexes
 */
export const getUserTrafficIndexes = async (): Promise<TrafficIndex[]> => {
  const response = await apiClient.get<{ success: boolean; data: TrafficIndex[] }>(
    '/traffic-indexes'
  );
  return response.data.data;
};

/**
 * Updates an existing traffic index
 * @param id - The traffic index ID
 * @param data - The update data
 * @returns Promise resolving to the updated traffic index
 */
export const updateTrafficIndex = async (
  id: string,
  data: UpdateTrafficIndexDto
): Promise<TrafficIndex> => {
  const response = await apiClient.put<{ success: boolean; data: TrafficIndex }>(
    `/traffic-indexes/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Deletes a traffic index
 * @param id - The traffic index ID
 * @returns Promise resolving when the deletion is complete
 */
export const deleteTrafficIndex = async (id: string): Promise<void> => {
  await apiClient.delete(`/traffic-indexes/${id}`);
};

// ==============================================
// Permission API Functions
// ==============================================

/**
 * Get all permissions for a traffic index
 * @param trafficIndexId - The traffic index ID
 * @returns Promise resolving to an array of permissions
 */
export const getPermissions = async (
  trafficIndexId: string
): Promise<TrafficIndexPermission[]> => {
  const response = await apiClient.get<{ success: boolean; data: TrafficIndexPermission[] }>(
    `/traffic-indexes/${trafficIndexId}/permissions`
  );
  return response.data.data;
};

/**
 * Add a permission for a user
 * @param trafficIndexId - The traffic index ID
 * @param data - The permission data
 * @returns Promise resolving to the created permission
 */
export const addPermission = async (
  trafficIndexId: string,
  data: AddPermissionDto
): Promise<TrafficIndexPermission> => {
  const response = await apiClient.post<{ success: boolean; data: TrafficIndexPermission }>(
    `/traffic-indexes/${trafficIndexId}/permissions`,
    data
  );
  return response.data.data;
};

/**
 * Update a permission
 * @param trafficIndexId - The traffic index ID
 * @param permissionId - The permission ID
 * @param data - The update data
 * @returns Promise resolving to the updated permission
 */
export const updatePermission = async (
  trafficIndexId: string,
  permissionId: string,
  data: UpdatePermissionDto
): Promise<TrafficIndexPermission> => {
  const response = await apiClient.put<{ success: boolean; data: TrafficIndexPermission }>(
    `/traffic-indexes/${trafficIndexId}/permissions/${permissionId}`,
    data
  );
  return response.data.data;
};

/**
 * Remove a permission
 * @param trafficIndexId - The traffic index ID
 * @param permissionId - The permission ID
 * @returns Promise resolving when the deletion is complete
 */
export const removePermission = async (
  trafficIndexId: string,
  permissionId: string
): Promise<void> => {
  await apiClient.delete(`/traffic-indexes/${trafficIndexId}/permissions/${permissionId}`);
};

// ==============================================
// Global Traffic Index API Functions
// ==============================================

/**
 * Get the global traffic index (visible to all users)
 * @returns Promise resolving to the global traffic index or null if none exists
 */
export const getGlobalTrafficIndex = async (): Promise<TrafficIndex | null> => {
  const response = await apiClient.get<{ success: boolean; data: TrafficIndex | null }>(
    '/traffic-indexes/global'
  );
  return response.data.data;
};

/**
 * Update the global traffic index
 * @param data - The update data
 * @returns Promise resolving to the updated global traffic index
 */
export const updateGlobalTrafficIndex = async (
  data: UpdateTrafficIndexDto
): Promise<TrafficIndex> => {
  const response = await apiClient.put<{ success: boolean; data: TrafficIndex }>(
    '/traffic-indexes/global',
    data
  );
  return response.data.data;
};
